import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3, BookOpen, FileSearch, FileText, Library, RefreshCw, Trophy, Upload, UserCog, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface DashboardProps {
  audience?: "user" | "admin";
}

export const Dashboard = ({ audience = "user" }: DashboardProps) => {
  const { t } = useTranslation();

  const userFeatures = [
    {
      icon: Users,
      titleKey: "dashboard.exploreCategory",
      descKey: "dashboard.exploreCategoryDesc",
      color: "hsl(280, 85%, 65%)",
      to: "/dashboard?tab=category",
    },
    {
      icon: Trophy,
      titleKey: "dashboard.strengthsExplorer",
      descKey: "dashboard.strengthsExplorerDesc",
      color: "hsl(45, 95%, 55%)",
      to: "/dashboard?tab=explorer",
    },
    {
      icon: Users,
      titleKey: "dashboard.teamStrengthsMap",
      descKey: "dashboard.teamStrengthsMapDesc",
      color: "hsl(205, 85%, 55%)",
      to: "/team-strengths-map",
    },
    {
      icon: BarChart3,
      titleKey: "dashboard.myStrengthsProfile",
      descKey: "dashboard.myStrengthsProfileDesc",
      color: "hsl(180, 80%, 55%)",
      to: "/dashboard?tab=charts",
    },
    {
      icon: Upload,
      titleKey: "dashboard.uploadCliftonReport",
      descKey: "dashboard.uploadCliftonReportDesc",
      color: "hsl(160, 75%, 50%)",
      to: "/dashboard?tab=upload",
    },
    {
      icon: RefreshCw,
      titleKey: "dashboard.updateMyReport",
      descKey: "dashboard.updateMyReportDesc",
      color: "hsl(210, 90%, 60%)",
      to: "/dashboard?tab=upload",
    },
    {
      icon: FileSearch,
      titleKey: "dashboard.reportStatus",
      descKey: "dashboard.reportStatusDesc",
      color: "hsl(340, 85%, 60%)",
      to: "/dashboard?tab=upload",
    },
  ];

  const adminFeatures = [
    {
      icon: UserCog,
      titleKey: "dashboard.userManagement",
      descKey: "dashboard.userManagementDesc",
      color: "hsl(280, 85%, 65%)",
      to: "/admin?tab=users",
    },
    {
      icon: Upload,
      titleKey: "dashboard.manageCliftonReports",
      descKey: "dashboard.manageCliftonReportsDesc",
      color: "hsl(180, 80%, 55%)",
      to: "/admin?tab=pdfreports",
    },
    {
      icon: FileText,
      titleKey: "dashboard.reportsReview",
      descKey: "dashboard.reportsReviewDesc",
      color: "hsl(160, 75%, 50%)",
      to: "/admin?tab=pdfreports",
    },
    {
      icon: BookOpen,
      titleKey: "dashboard.userDirectory",
      descKey: "dashboard.userDirectoryDesc",
      color: "hsl(25, 95%, 60%)",
      to: "/admin?tab=users",
    },
    {
      icon: Trophy,
      titleKey: "dashboard.strengthsLeaderboard",
      descKey: "dashboard.strengthsLeaderboardDesc",
      color: "hsl(45, 95%, 55%)",
      to: "/admin?tab=leaderboard",
    },
  ];

  const features = audience === "admin" ? adminFeatures : userFeatures;
  const isAdminAudience = audience === "admin";

  return (
    <section className={isAdminAudience ? "relative px-0 py-8 sm:py-14 lg:py-20" : "relative px-0 py-8 sm:py-14 lg:py-20"}>
      <div className="container mx-auto max-w-7xl min-w-0">
        <div className={isAdminAudience ? "text-center space-y-4 mb-8 animate-fade-in sm:mb-12 lg:mb-16" : "text-center space-y-4 mb-8 animate-fade-in sm:mb-12 lg:mb-16"}>
          <h2 className={isAdminAudience ? "text-3xl font-bold sm:text-4xl lg:text-5xl xl:text-6xl" : "text-3xl font-bold sm:text-4xl lg:text-5xl xl:text-6xl"}>
            <span className="text-gradient">{t('dashboard.featuresTitle')}</span>
          </h2>
          <p className={isAdminAudience ? "text-base text-foreground/80 max-w-2xl mx-auto font-medium sm:text-xl" : "text-base text-foreground/80 max-w-2xl mx-auto font-medium sm:text-xl"}>
            {t('dashboard.featuresSubtitle')}
          </p>
        </div>

        <div className={isAdminAudience ? "grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 sm:gap-6" : "grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 sm:gap-6"}>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const cardContent = (
              <Card
                className={`${isAdminAudience ? "p-5 sm:p-8" : "p-5 sm:p-8"} h-full bg-card border-border shadow-card hover:shadow-glow hover:border-primary/30 transition-all duration-300 animate-scale-in group`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex flex-col items-start gap-4 h-full">
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    style={{ 
                      background: `${feature.color}20`,
                      boxShadow: `0 0 20px ${feature.color}30`,
                    }}
                  >
                    <Icon 
                      className="w-7 h-7 transition-all duration-300" 
                      style={{ color: feature.color }}
                    />
                  </div>
                  
                  <div className="flex-grow">
                    <h3 className={`${isAdminAudience ? "text-lg sm:text-xl" : "text-lg sm:text-xl"} font-bold text-foreground mb-3 group-hover:text-primary transition-colors`}>
                      {t(feature.titleKey)}
                    </h3>
                    <p className="text-foreground/75 leading-relaxed font-medium">
                      {t(feature.descKey)}
                    </p>
                  </div>
                  
                  <Badge
                    className="mt-2 pointer-events-none transition-all duration-300"
                    style={{ 
                      background: `${feature.color}15`,
                      color: 'hsl(0, 0%, 0%)',
                      borderColor: `${feature.color}30`,
                    }}
                  >
                    {t('dashboard.learnMore')}
                  </Badge>
                </div>
              </Card>
            );

            return feature.to ? (
              <Link
                key={index}
                to={feature.to}
                className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                {cardContent}
              </Link>
            ) : (
              <div key={index}>{cardContent}</div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
